import React, { useEffect, useState } from "react";
import { getAuth, signOut } from "firebase/auth";
import { collection, deleteField, doc, getDocs, query, serverTimestamp, where, writeBatch } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import { LineWave } from "react-loader-spinner";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  FormControl,
  FormErrorMessage,
  Input,
  ModalFooter,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { db, functions } from "./firebase";
import "./UpdateDashboard.css";
import PortalHeader from './PortalHeader';
import { findAppUpdateCandidates } from './appUpdateCandidates';

const APP_UPDATE_COLLECTION = "AppUpdate";
const MDT_OFFSET = "-06:00";

const createRuleTemplate = () => ({
  minimumSupportedVersion: "",
  minimumSupportedVersionCode: "",
  minimumSupportedEffectiveDate: "",
  whatsNew: "",
});

const createPlatformState = (platform) => ({
  enabled: false,
  sourceId: "",
  identifier: "",
  displayName: "",
  latestVersion: "",
  latestVersionCode: "",
  latestVersionCreatedAt: "",
  storeUrl: "",
  lastSyncedAt: "",
  rule: createRuleTemplate(),
});

const slugify = (value) => String(value || "")
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");

const buildUniqueAppId = (name, apps, excludeAppIndex = null) => {
  const baseSlug = slugify(name);

  if (!baseSlug) return "";

  const usedIds = new Set(
    apps
      .map((app, index) => (index === excludeAppIndex ? null : app.id))
      .filter(Boolean)
  );

  if (!usedIds.has(baseSlug)) {
    return baseSlug;
  }

  let suffix = 2;
  let nextSlug = `${baseSlug}-${suffix}`;

  while (usedIds.has(nextSlug)) {
    suffix += 1;
    nextSlug = `${baseSlug}-${suffix}`;
  }

  return nextSlug;
};

const buildUniqueProjectId = (name, projects, excludeProjectIndex = null) => {
  const baseSlug = slugify(name);

  if (!baseSlug) return "";

  const usedIds = new Set(
    projects
      .map((project, index) => (index === excludeProjectIndex ? null : project.id))
      .filter(Boolean)
  );

  if (!usedIds.has(baseSlug)) {
    return baseSlug;
  }

  let suffix = 2;
  let nextSlug = `${baseSlug}-${suffix}`;

  while (usedIds.has(nextSlug)) {
    suffix += 1;
    nextSlug = `${baseSlug}-${suffix}`;
  }

  return nextSlug;
};

const hasProjectSlugConflict = (name, projects, excludeProjectIndex = null) => {
  const nextSlug = slugify(name);

  if (!nextSlug) return false;

  return projects.some((project, index) => index !== excludeProjectIndex && project.id === nextSlug);
};

const hasAppSlugConflict = (name, apps, excludeAppIndex = null) => {
  const nextSlug = slugify(name);

  if (!nextSlug) return false;

  return apps.some((app, index) => index !== excludeAppIndex && app.id === nextSlug);
};

const createAppState = () => ({
  id: `app-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: "",
  appKey: "",
  description: "",
  platforms: {
    ios: createPlatformState("ios"),
    android: createPlatformState("android"),
  },
});

const createProjectState = () => ({
  id: `project-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: "",
  description: "",
  apps: [],
});

const normalizeRule = (rule) => ({
  minimumSupportedVersion: typeof rule?.minimumSupportedVersion === "string" ? rule.minimumSupportedVersion : "",
  minimumSupportedVersionCode: typeof rule?.minimumSupportedVersionCode === "string" ? rule.minimumSupportedVersionCode : "",
  minimumSupportedEffectiveDate: typeof rule?.minimumSupportedEffectiveDate === "string" ? rule.minimumSupportedEffectiveDate : "",
  recommendedVersion: typeof rule?.recommendedVersion === "string" ? rule.recommendedVersion : "",
  recommendedEffectiveDate: typeof rule?.recommendedEffectiveDate === "string" ? rule.recommendedEffectiveDate : "",
  whatsNew: typeof rule?.whatsNew === "string"
    ? rule.whatsNew
    : typeof rule?.whatsnew === "string"
      ? rule.whatsnew
      : typeof rule?.releaseNotes === "string"
        ? rule.releaseNotes
        : typeof rule?.releaseNote === "string"
          ? rule.releaseNote
          : typeof rule?.message === "string"
            ? rule.message
            : "",
});

const isRuleEmpty = (rule) => {
  const normalizedRule = normalizeRule(rule);

  return !normalizedRule.minimumSupportedVersion &&
    !normalizedRule.minimumSupportedVersionCode &&
    !normalizedRule.whatsNew;
};

const formatRuleWithEffectiveDate = (version, effectiveDate) => {
  if (!version) return "Not set";
  if (!effectiveDate) return version;
  return `${version} (Effective: ${effectiveDate})`;
};

const formatVersionWithCode = (version, versionCode) => {
  const normalizedVersion = typeof version === "string" ? version.trim() : "";
  const normalizedVersionCode = typeof versionCode === "string" ? versionCode.trim() : "";

  if (normalizedVersion && normalizedVersionCode) {
    return `${normalizedVersion} (${normalizedVersionCode})`;
  }

  return normalizedVersion || normalizedVersionCode || "-";
};

const getEffectiveDatePart = (value) => {
  if (!value) return "";
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?/);
  return match?.[1] || "";
};

const getEffectiveTimePart = (value) => {
  if (!value) return "";
  const match = String(value).match(/^\d{4}-\d{2}-\d{2}T(\d{2}:\d{2})/);
  return match?.[1] || "";
};

const mergeEffectiveDateTime = (date, time) => {
  if (date && time) return `${date}T${time}`;
  if (date) return date;
  if (time) return `T${time}`;
  return "";
};

const updateEffectiveDateTimePart = (value, part, nextPartValue) => {
  const currentDate = getEffectiveDatePart(value);
  const currentTime = getEffectiveTimePart(value);

  return mergeEffectiveDateTime(
    part === "date" ? nextPartValue : currentDate,
    part === "time" ? nextPartValue : currentTime
  );
};

const hasCompleteEffectiveDateTime = (value) => Boolean(
  getEffectiveDatePart(value) && getEffectiveTimePart(value)
);

const hasPartialEffectiveDateTime = (value) => Boolean(value) && !hasCompleteEffectiveDateTime(value);

const formatEffectiveDateLabel = (value) => hasCompleteEffectiveDateTime(value) ?
  `${getEffectiveDatePart(value)} ${getEffectiveTimePart(value)} MDT` :
  "";

const getPlatformLastUpdatedDisplay = (platformConfig) => {
  const lastUpdatedValue = platformConfig?.latestVersionCreatedAt || platformConfig?.lastSyncedAt || "";

  if (!lastUpdatedValue) {
    return "Never";
  }

  return new Date(lastUpdatedValue).toLocaleString("en-US");
};

const isRuleActiveNow = (version, effectiveDate) => {
  if (!version) return false;
  if (!effectiveDate) return true;

  const effectiveAt = new Date(effectiveDate);

  if (Number.isNaN(effectiveAt.getTime())) {
    return false;
  }

  return Date.now() >= effectiveAt.getTime();
};

const getEffectiveRuleSummary = (rule, latestVersion, latestVersionCode, platform) => {
  const normalizedRule = normalizeRule(rule);
  const minimumSupportedVersion = normalizedRule.minimumSupportedVersion;
  const minimumSupportedVersionCode = normalizedRule.minimumSupportedVersionCode;
  const minimumSupportedEffectiveDate = normalizedRule.minimumSupportedEffectiveDate;
  const minimumSupportedIsActive = isRuleActiveNow(
    minimumSupportedVersion,
    minimumSupportedEffectiveDate
  );

  if (minimumSupportedIsActive) {
    return {
      activeRuleLabel: "Required update",
      activeVersionLabel: formatRuleWithEffectiveDate(
        platform === "android"
          ? formatVersionWithCode(minimumSupportedVersion, minimumSupportedVersionCode)
          : minimumSupportedVersion,
        formatEffectiveDateLabel(minimumSupportedEffectiveDate)
      ),
      whatsNew: normalizedRule.whatsNew || "Not set",
    };
  }

  return {
    activeRuleLabel: "Recommended update",
    activeVersionLabel: platform === "android"
      ? formatVersionWithCode(latestVersion, latestVersionCode)
      : latestVersion || "-",
    whatsNew: normalizedRule.whatsNew || "Not set",
  };
};

const serializeEffectiveDateTimeForSave = (value) => {
  if (!hasCompleteEffectiveDateTime(value)) {
    return value || "";
  }

  const date = getEffectiveDatePart(value);
  const time = getEffectiveTimePart(value);
  return `${date}T${time}:00${MDT_OFFSET}`;
};

const sanitizeRuleForSave = (rule) => ({
  minimumSupportedVersion: typeof rule?.minimumSupportedVersion === "string" ? rule.minimumSupportedVersion : "",
  minimumSupportedVersionCode: typeof rule?.minimumSupportedVersionCode === "string" ? rule.minimumSupportedVersionCode : "",
  minimumSupportedEffectiveDate: serializeEffectiveDateTimeForSave(rule?.minimumSupportedEffectiveDate),
  whatsNew: typeof rule?.whatsNew === "string" ? rule.whatsNew : "",
});

const sanitizePlatformForSave = (platform) => ({
  ...platform,
  rule: sanitizeRuleForSave(platform?.rule || {}),
});

const sanitizeProjectForSave = (project) => ({
  ...project,
  apps: Array.isArray(project?.apps)
    ? project.apps.map((app) => ({
        ...app,
        platforms: {
          ios: sanitizePlatformForSave(app?.platforms?.ios || {}),
          android: sanitizePlatformForSave(app?.platforms?.android || {}),
        },
      }))
    : [],
});

const validateRuleEffectiveDateTimes = (rule, label, platform) => {
  if (hasPartialEffectiveDateTime(rule?.minimumSupportedEffectiveDate)) {
    throw new Error(`${label}: enter both a date and time for the required update.`);
  }

  if (platform === "android") {
    const normalizedRule = normalizeRule(rule);
    const hasVersionName = Boolean(normalizedRule.minimumSupportedVersion);
    const hasVersionCode = Boolean(normalizedRule.minimumSupportedVersionCode);

    if (hasVersionName !== hasVersionCode) {
      throw new Error(`${label}: enter both the minimum supported Android version name and version code.`);
    }
  }
};

const validateProjectsEffectiveDateTimes = (projects) => {
  projects.forEach((project) => {
    ["ios", "android"].forEach((platform) => {
      const platformLabel = platform === "ios" ? "iOS" : "Android";
      project.apps?.forEach((app) => {
        validateRuleEffectiveDateTimes(app.platforms?.[platform]?.rule, `${app.name || app.id} ${platformLabel} app rule`, platform);
      });
    });
  });
};

const normalizePlatform = (platform, value, fallbackRule = null) => {
  const directRule = normalizeRule(value?.rule);
  const resolvedRule = isRuleEmpty(directRule) ? normalizeRule(fallbackRule) : directRule;

  return ({
  enabled: value?.enabled !== false,
  sourceId: typeof value?.sourceId === "string" ? value.sourceId : "",
  identifier: typeof value?.identifier === "string" ? value.identifier : "",
  displayName: typeof value?.displayName === "string" ? value.displayName : "",
  latestVersion: typeof value?.latestVersion === "string" ? value.latestVersion : "",
  latestVersionCode: typeof value?.latestVersionCode === "string" ? value.latestVersionCode : "",
  latestVersionCreatedAt: typeof value?.latestVersionCreatedAt === "string" ? value.latestVersionCreatedAt : "",
  storeUrl: typeof value?.storeUrl === "string" ? value.storeUrl : "",
  lastSyncedAt: typeof value?.lastSyncedAt === "string" ? value.lastSyncedAt : "",
  rule: resolvedRule,
});
};

const normalizeApp = (app, index = 0, fallbackRules = null) => ({
  id: typeof app?.id === "string" && app.id.trim() ? app.id : `app-${index + 1}`,
  name: typeof app?.name === "string" ? app.name : "",
  appKey: typeof app?.appKey === "string" ? app.appKey : "",
  description: typeof app?.description === "string" ? app.description : "",
  platforms: {
    ios: normalizePlatform("ios", app?.platforms?.ios, fallbackRules?.ios),
    android: normalizePlatform("android", app?.platforms?.android, fallbackRules?.android),
  },
});

const getAppDisplayName = (app) => `${app?.platforms?.android?.sourceId || ""} - ${app?.name || ""}`;

const normalizeProject = (project, index = 0) => {
  const fallbackRules = {
    ios: normalizeRule(project?.globalRules?.ios || project?.projectRules?.ios),
    android: normalizeRule(project?.globalRules?.android || project?.projectRules?.android),
  };

  return ({
  id: typeof project?.id === "string" && project.id.trim() ? project.id : `project-${index + 1}`,
  name: typeof project?.name === "string" ? project.name : "",
  description: typeof project?.description === "string" ? project.description : "",
  updatedAt: normalizeUpdatedAt(project?.updatedAt),
  updatedBy: typeof project?.updatedBy === "string" ? project.updatedBy : null,
  apps: Array.isArray(project?.apps)
    ? project.apps.map((app, appIndex) => normalizeApp(app, appIndex, fallbackRules))
    : [],
});
};

const normalizeUpdatedAt = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value?.toDate === "function") return value.toDate().toISOString();
  return null;
};

const getLatestProjectMeta = (projects) => {
  const latestProject = [...projects]
    .filter((project) => project?.updatedAt)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];

  return {
    updatedAt: latestProject?.updatedAt || null,
    updatedBy: latestProject?.updatedBy || null,
  };
};

const normalizeDashboardConfig = (projects) => {
  const normalizedProjects = Array.isArray(projects) && projects.length > 0
    ? projects.map((project, index) => normalizeProject(project, index))
    : [];
  const latestMeta = getLatestProjectMeta(normalizedProjects);

  return {
    schemaVersion: 2,
    storage: "firestore",
    projects: normalizedProjects,
    updatedAt: latestMeta.updatedAt,
    updatedBy: latestMeta.updatedBy,
  };
};

const mergePlatformIntoProjects = (projects, projectIndex, appIndex, platform, nextPlatform) => (
  (Array.isArray(projects) ? projects : []).map((project, currentProjectIndex) => {
    if (currentProjectIndex !== projectIndex) {
      return project;
    }

    return {
      ...project,
      apps: (project.apps || []).map((app, currentAppIndex) => {
        if (currentAppIndex !== appIndex) {
          return app;
        }

        return {
          ...app,
          platforms: {
            ...app.platforms,
            [platform]: nextPlatform,
          },
        };
      }),
    };
  })
);

const formatSyncFailureSummary = (failures) => {
  if (!Array.isArray(failures) || failures.length === 0) {
    return "";
  }

  const maxItems = 5;
  const visibleFailures = failures.slice(0, maxItems);
  const remainingCount = failures.length - visibleFailures.length;
  const suffix = remainingCount > 0 ? ` and ${remainingCount} more` : "";

  return `${visibleFailures.join(", ")}${suffix}`;
};

const UpdateDashboard = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [config, setConfig] = useState(() => normalizeDashboardConfig(null));
  const [searchTerm, setSearchTerm] = useState("");
  const [appSortDirection, setAppSortDirection] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncingKey, setSyncingKey] = useState("");
  const [selectedAppEditor, setSelectedAppEditor] = useState(null);
  const [selectedAppInitialSnapshot, setSelectedAppInitialSnapshot] = useState("");
  const [confirmCloseAppEditorOpen, setConfirmCloseAppEditorOpen] = useState(false);
  const [selectedBulkAppsByProject, setSelectedBulkAppsByProject] = useState({});
  const [bulkRuleDraftsByProject, setBulkRuleDraftsByProject] = useState({});
  const [expandedBulkApplyByProject, setExpandedBulkApplyByProject] = useState({});
  const [pendingBulkApply, setPendingBulkApply] = useState(null);
  const [removeAppTarget, setRemoveAppTarget] = useState(null);
  const [removeAppConfirmation, setRemoveAppConfirmation] = useState("");
  const [removeAppTouched, setRemoveAppTouched] = useState(false);
  const [removeProjectTarget, setRemoveProjectTarget] = useState(null);
  const [removeProjectConfirmation, setRemoveProjectConfirmation] = useState("");
  const [removeProjectTouched, setRemoveProjectTouched] = useState(false);
  const [newProjectDialogOpen, setNewProjectDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectNameTouched, setNewProjectNameTouched] = useState(false);
  const [newAppDialog, setNewAppDialog] = useState(null);
  const [newAppName, setNewAppName] = useState("");
  const [newAppNameTouched, setNewAppNameTouched] = useState(false);
  const [newAppStoreId, setNewAppStoreId] = useState("");
  const [newAppStoreIdTouched, setNewAppStoreIdTouched] = useState(false);
  const [newAppAndroidId, setNewAppAndroidId] = useState("");
  const [newAppAndroidIdTouched, setNewAppAndroidIdTouched] = useState(false);
  const [error, setError] = useState("");
  const [releaseApps, setReleaseApps] = useState([]);
  const [releaseAppsLoading, setReleaseAppsLoading] = useState(true);
  const [releaseAppsError, setReleaseAppsError] = useState("");

  useEffect(() => {
    let active = true;
    getDocs(collection(db, "App"))
      .then((snapshot) => {
        if (active) setReleaseApps(snapshot.docs.map((appDoc) => ({ ...appDoc.data(), id: appDoc.id })));
      })
      .catch(() => {
        if (active) setReleaseAppsError("Unable to compare with the release dashboard. Reload to try again.");
      })
      .finally(() => {
        if (active) setReleaseAppsLoading(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const projectsSnapshot = await getDocs(collection(db, APP_UPDATE_COLLECTION));
        const projects = await Promise.all(
          projectsSnapshot.docs.map(async (projectDoc) => {
            const appsSnapshot = await getDocs(collection(projectDoc.ref, "apps"));

            return normalizeProject({
              id: projectDoc.id,
              ...projectDoc.data(),
              globalRules: projectDoc.data()?.projectRules,
              apps: appsSnapshot.docs.map((appDoc) => ({
                id: appDoc.id,
                ...appDoc.data(),
              })),
            });
          })
        );
        const normalizedConfig = normalizeDashboardConfig(projects);
        setConfig(normalizedConfig);
      } catch (loadError) {
        console.error("Failed to load update dashboard:", loadError);
        setError(loadError.message || "Unable to load update dashboard.");
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth).then(() => {
      window.location.href = "/login";
    }).catch((logoutError) => {
      console.error("Error signing out: ", logoutError);
    });
  };

  const updateProjectField = (projectIndex, field, value) => {
    setConfig((current) => ({
      ...current,
      projects: current.projects.map((project, index) => {
        if (index !== projectIndex) return project;

        const nextProject = { ...project, [field]: value };

        if (field === "name") {
          if (hasProjectSlugConflict(value, current.projects, projectIndex)) {
            return project;
          }

          const nextId = buildUniqueProjectId(value, current.projects, projectIndex);
          return {
            ...nextProject,
            id: nextId || project.id,
          };
        }

        return nextProject;
      }),
    }));
  };

  const updateGlobalRuleField = (projectIndex, platform, field, value) => {
    setBulkRuleDraftsByProject((current) => {
      const project = config.projects?.[projectIndex];
      const projectId = project?.id;

      if (!projectId) {
        return current;
      }

      return {
        ...current,
        [projectId]: {
          ios: createRuleTemplate(),
          android: createRuleTemplate(),
          ...current[projectId],
          [platform]: {
            ...createRuleTemplate(),
            ...current[projectId]?.[platform],
            [field]: value,
          },
        },
      };
    });
  };

  const updateAppField = (projectIndex, appIndex, field, value) => {
    setConfig((current) => ({
      ...current,
      projects: current.projects.map((project, currentProjectIndex) => {
        if (currentProjectIndex !== projectIndex) return project;

        return {
          ...project,
          apps: project.apps.map((app, currentAppIndex) => {
            if (currentAppIndex !== appIndex) return app;

            const nextApp = { ...app, [field]: value };

            if (field === "name") {
              if (hasAppSlugConflict(value, project.apps, appIndex)) {
                return app;
              }

              const nextId = buildUniqueAppId(value, project.apps, appIndex);
              return {
                ...nextApp,
                id: nextId || app.id,
              };
            }

            return nextApp;
          }),
        };
      }),
    }));
  };

  const updatePlatformField = (projectIndex, appIndex, platform, field, value) => {
    setConfig((current) => ({
      ...current,
      projects: current.projects.map((project, currentProjectIndex) => {
        if (currentProjectIndex !== projectIndex) return project;

        return {
          ...project,
          apps: project.apps.map((app, currentAppIndex) => {
            if (currentAppIndex !== appIndex) return app;

            return {
              ...app,
              platforms: {
                ...app.platforms,
                [platform]: {
                  ...app.platforms[platform],
                  [field]: value,
                },
              },
            };
          }),
        };
      }),
    }));
  };

  const updatePlatformRuleField = (projectIndex, appIndex, platform, field, value) => {
    setConfig((current) => ({
      ...current,
      projects: current.projects.map((project, currentProjectIndex) => {
        if (currentProjectIndex !== projectIndex) return project;

        return {
          ...project,
          apps: project.apps.map((app, currentAppIndex) => {
            if (currentAppIndex !== appIndex) return app;

            return {
              ...app,
              platforms: {
                ...app.platforms,
                [platform]: {
                  ...app.platforms[platform],
                  rule: {
                    ...app.platforms[platform].rule,
                    [field]: value,
                  },
                },
              },
            };
          }),
        };
      }),
    }));
  };

  const getBulkRuleDraft = (projectId, platform) => ({
    ...createRuleTemplate(),
    ...bulkRuleDraftsByProject?.[projectId]?.[platform],
  });

  const getSelectedBulkAppIds = (projectId) => selectedBulkAppsByProject?.[projectId] || [];

  const toggleBulkAppSelection = (projectId, appId) => {
    setSelectedBulkAppsByProject((current) => {
      const currentSelection = current?.[projectId] || [];
      const nextSelection = currentSelection.includes(appId)
        ? currentSelection.filter((id) => id !== appId)
        : [...currentSelection, appId];

      return {
        ...current,
        [projectId]: nextSelection,
      };
    });
  };

  const toggleSelectAllBulkApps = (projectId, apps) => {
    setSelectedBulkAppsByProject((current) => {
      const appIds = apps.map((app) => app.id).filter(Boolean);
      const currentSelection = current?.[projectId] || [];
      const allSelected = appIds.length > 0 && appIds.every((id) => currentSelection.includes(id));

      return {
        ...current,
        [projectId]: allSelected ? [] : appIds,
      };
    });
  };

  const buildBulkAppliedProject = (projectIndex, platform) => {
    const project = config.projects?.[projectIndex];
    const projectId = project?.id;

    if (!project || !projectId) {
      throw new Error("Project not found.");
    }

    const selectedAppIds = getSelectedBulkAppIds(projectId);

    if (selectedAppIds.length === 0) {
      throw new Error("Select at least one app before applying update rules.");
    }

    const bulkRule = getBulkRuleDraft(projectId, platform);
    validateRuleEffectiveDateTimes(bulkRule, `${project.name || project.id} ${platform === "ios" ? "iOS" : "Android"} bulk rule`, platform);

    return {
      project,
      projectId,
      selectedAppIds,
      bulkRule,
      nextProject: {
        ...project,
        apps: project.apps.map((app) => (
          selectedAppIds.includes(app.id)
            ? {
                ...app,
                platforms: {
                  ...app.platforms,
                  [platform]: {
                    ...app.platforms[platform],
                    rule: {
                      ...app.platforms[platform].rule,
                      ...bulkRule,
                    },
                  },
                },
              }
            : app
        )),
      },
    };
  };

  const requestBulkApplyToSelectedApps = (projectIndex, platform) => {
    const { project, selectedAppIds } = buildBulkAppliedProject(projectIndex, platform);
    const selectedApps = project.apps.filter((app) => selectedAppIds.includes(app.id));

    setPendingBulkApply({
      projectIndex,
      platform,
      appNames: selectedApps.map((app) => app.name || app.id),
      count: selectedApps.length,
    });
  };

  const confirmBulkApplyToSelectedApps = async () => {
    if (!pendingBulkApply) {
      return;
    }

    const { projectIndex, platform } = pendingBulkApply;
    const { nextProject, selectedAppIds } = buildBulkAppliedProject(projectIndex, platform);

    setSaving(true);
    setError("");

    try {
      await persistProject(sanitizeProjectForSave(nextProject), true);
      setPendingBulkApply(null);
      toast({
        position: "top",
        title: "Update rules applied",
        description: `Applied ${platform === "ios" ? "iOS" : "Android"} update rules to ${selectedAppIds.length} app${selectedAppIds.length === 1 ? "" : "s"}.`,
        status: "success",
        isClosable: true,
      });
    } catch (bulkError) {
      const message = bulkError.message || "Unable to apply update rules.";
      setError(message);
      toast({
        position: "top",
        title: "Unable to apply update rules",
        description: message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleBulkApplyPanel = (projectId) => {
    setExpandedBulkApplyByProject((current) => {
      const nextExpanded = !current?.[projectId];

      if (!nextExpanded) {
        setSelectedBulkAppsByProject((selectedCurrent) => ({
          ...selectedCurrent,
          [projectId]: [],
        }));
      }

      return {
        ...current,
        [projectId]: nextExpanded,
      };
    });
  };

  const syncIosMetadata = async (sourceId) => {
    const getAppleAppMetadataFn = httpsCallable(functions, "getAppleAppMetadata");
    const result = await getAppleAppMetadataFn({ sourceId: sourceId.trim() });
    return result.data || {};
  };

  const syncAndroidMetadata = async (sourceId) => {
    const appQuery = query(collection(db, "App"), where("appId", "==", sourceId.trim()));
    const snapshot = await getDocs(appQuery);

    if (snapshot.empty) {
      throw new Error("No Android release was found for this app ID.");
    }

    const appDoc = snapshot.docs[0].data();
    const identifier = appDoc.packageName || appDoc.bundleId || "";

    return {
      displayName: appDoc.appName || "",
      identifier,
      latestVersion: appDoc.versionName || "",
      latestVersionCode: appDoc.versionCode || "",
      latestVersionCreatedAt: appDoc.updatedTime?.toDate?.()?.toISOString?.() || "",
    };
  };

  const mergePlatformMetadata = (platformConfig, metadata) => {
    const { storeUrl: ignoredStoreUrl, ...metadataWithoutStoreUrl } = metadata || {};

    return {
      ...platformConfig,
      ...metadataWithoutStoreUrl,
      storeUrl: platformConfig?.storeUrl || "",
      lastSyncedAt: new Date().toISOString(),
    };
  };

  const persistSyncedPlatformMetadata = async (projectIndex, appIndex, platform, nextPlatform) => {
    const project = config.projects?.[projectIndex];
    const app = project?.apps?.[appIndex];

    if (!project || !app) {
      throw new Error("Unable to find the app to save its version information.");
    }

    const auth = getAuth();
    const updatedBy = auth.currentUser?.email || null;
    const projectRef = doc(db, APP_UPDATE_COLLECTION, project.id);
    const appRef = doc(collection(projectRef, "apps"), app.id);
    const batch = writeBatch(db);

    batch.set(projectRef, {
      updatedBy,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    batch.set(appRef, {
      platforms: {
        ...app.platforms,
        [platform]: sanitizePlatformForSave(nextPlatform),
      },
      updatedBy,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    await batch.commit();

    if (
      selectedAppEditor
      && selectedAppEditor.projectIndex === projectIndex
      && selectedAppEditor.appIndex === appIndex
      && selectedAppInitialSnapshot
    ) {
      setSelectedAppInitialSnapshot((currentSnapshot) => {
        if (!currentSnapshot) {
          return currentSnapshot;
        }

        const savedApp = JSON.parse(currentSnapshot);
        const nextSavedApp = normalizeApp({
          ...savedApp,
          platforms: {
            ...savedApp.platforms,
            [platform]: nextPlatform,
          },
        });

        return JSON.stringify(nextSavedApp);
      });
    }
  };

  const handlePlatformSync = async (projectIndex, appIndex, platform) => {
    const platformConfig = config.projects?.[projectIndex]?.apps?.[appIndex]?.platforms?.[platform];
    const sourceId = platformConfig?.sourceId?.trim?.() || "";

    if (!sourceId) {
      toast({
        position: "top",
        title: "Missing source ID",
        description: `Add the ${platform === "ios" ? "App Store app ID" : "Android app ID"} first.`,
        status: "warning",
        isClosable: true,
      });
      return;
    }

    const stateKey = `${projectIndex}-${appIndex}-${platform}`;
    setSyncingKey(stateKey);

    try {
      const metadata = platform === "ios"
        ? await syncIosMetadata(sourceId)
        : await syncAndroidMetadata(sourceId);

      const nextPlatform = mergePlatformMetadata(platformConfig, metadata);

      setConfig((current) => ({
        ...current,
        projects: mergePlatformIntoProjects(current.projects, projectIndex, appIndex, platform, nextPlatform),
      }));

      await persistSyncedPlatformMetadata(projectIndex, appIndex, platform, nextPlatform);

      toast({
        position: "top",
        title: `${platform === "ios" ? "iOS" : "Android"} metadata synced`,
        description: `${metadata.displayName || "App information refreshed."} Changes saved automatically.`,
        status: "success",
        isClosable: true,
      });
    } catch (syncError) {
      console.error(`Failed to sync ${platform} metadata:`, syncError);
      toast({
        position: "top",
        title: `Unable to sync ${platform === "ios" ? "iOS" : "Android"} app`,
        description: syncError.message || "Unable to synchronize version information. Try again.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setSyncingKey("");
    }
  };

  const handleSyncAllMetadata = async () => {
    setSaving(true);
    setError("");
    setSyncingKey("all");

    try {
      let syncedCount = 0;
      const failedFetches = [];
      const failedSaves = [];
      const successfulProjects = [];

      for (const project of config.projects) {
        const nextApps = [];
        let projectHasSyncedChanges = false;

        for (const app of project.apps || []) {
          const nextPlatforms = { ...app.platforms };

          for (const platform of ["ios", "android"]) {
            const platformConfig = app.platforms?.[platform] || createPlatformState(platform);
            const sourceId = platformConfig?.sourceId?.trim?.() || "";

            if (!sourceId) {
              continue;
            }

            try {
              const metadata = platform === "ios"
                ? await syncIosMetadata(sourceId)
                : await syncAndroidMetadata(sourceId);

              nextPlatforms[platform] = mergePlatformMetadata(platformConfig, metadata);
              syncedCount += 1;
              projectHasSyncedChanges = true;
            } catch (syncError) {
              failedFetches.push(`${project.name || project.id} / ${app.name || app.id} / ${platform === "ios" ? "iOS" : "Android"}`);
              console.error(`Failed to sync ${platform} metadata for ${app.name || app.id}:`, syncError);
            }
          }

          nextApps.push({
            ...app,
            platforms: nextPlatforms,
          });
        }

        const nextProject = {
          ...project,
          apps: nextApps,
        };

        if (!projectHasSyncedChanges) {
          successfulProjects.push(project);
          continue;
        }

        try {
          // Save each project independently so one broken app does not block the rest.
          await persistProject(sanitizeProjectForSave(nextProject), false);
          successfulProjects.push(sanitizeProjectForSave(nextProject));
        } catch (saveError) {
          failedSaves.push(project.name || project.id);
          successfulProjects.push(project);
          console.error(`Failed to save synced metadata for ${project.name || project.id}:`, saveError);
        }
      }

      setConfig(normalizeDashboardConfig(successfulProjects));

      if (failedFetches.length > 0 || failedSaves.length > 0) {
        const fetchSummary = formatSyncFailureSummary(failedFetches);
        const saveSummary = formatSyncFailureSummary(failedSaves);
        const descriptionParts = [
          `Synced ${syncedCount} app platform${syncedCount === 1 ? "" : "s"}.`,
          failedFetches.length > 0 ? `Fetch issues: ${fetchSummary}.` : "",
          failedSaves.length > 0 ? `Save issues: ${saveSummary}.` : "",
        ].filter(Boolean);

        toast({
          position: "top",
          title: "Sync completed with issues",
          description: descriptionParts.join(" "),
          status: "warning",
          isClosable: true,
        });
      } else {
        toast({
          position: "top",
          title: "All app metadata synced",
          description: `Synced and saved ${syncedCount} app platform${syncedCount === 1 ? "" : "s"}.`,
          status: "success",
          isClosable: true,
        });
      }
    } catch (syncError) {
      console.error("Failed to sync all app metadata:", syncError);
      const message = syncError.message || "Unable to sync all app metadata.";
      setError(message);
      toast({
        position: "top",
        title: "Sync failed",
        description: message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
      setSyncingKey("");
    }
  };

  const openRemoveProjectConfirmation = (projectIndex) => {
    setRemoveProjectTarget({ projectIndex });
    setRemoveProjectConfirmation("");
    setRemoveProjectTouched(false);
  };

  const closeRemoveProjectConfirmation = () => {
    setRemoveProjectTarget(null);
    setRemoveProjectConfirmation("");
    setRemoveProjectTouched(false);
  };

  const confirmRemoveProject = async () => {
    const projectName = removeProjectItem?.name?.trim() || "";
    const typedName = removeProjectConfirmation.trim();

    if (!removeProjectTarget || !projectName || typedName !== projectName) {
      setRemoveProjectTouched(true);
      return;
    }

    try {
      setSaving(true);
      setError("");

      const nextProjects = config.projects.filter((_, index) => index !== removeProjectTarget.projectIndex);
      const normalizedProjects = nextProjects;

      await persistProjects(normalizedProjects, true);
      closeRemoveProjectConfirmation();
    } catch (saveError) {
      console.error("Failed to remove project:", saveError);
      const message = saveError.message || "Unable to remove project.";
      setError(message);
      toast({
        position: "top",
        title: "Unable to remove item",
        description: message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const openAddProjectDialog = () => {
    setNewProjectDialogOpen(true);
    setNewProjectName("");
    setNewProjectNameTouched(false);
  };

  const closeAddProjectDialog = () => {
    setNewProjectDialogOpen(false);
    setNewProjectName("");
    setNewProjectNameTouched(false);
  };

  const confirmAddProject = async () => {
    const trimmedName = newProjectName.trim();

    if (!trimmedName) {
      setNewProjectNameTouched(true);
      return;
    }

    try {
      setSaving(true);
      setError("");

      if (hasProjectSlugConflict(trimmedName, config.projects)) {
        throw new Error("A project with this name already exists.");
      }

      const nextProject = createProjectState();

      const nextProjects = [
        ...config.projects,
        {
          ...nextProject,
          id: buildUniqueProjectId(trimmedName, config.projects) || nextProject.id,
          name: trimmedName,
        },
      ];

      await persistProjects(nextProjects, true);
      closeAddProjectDialog();
    } catch (saveError) {
      console.error("Failed to add project:", saveError);
      const message = saveError.message || "Unable to add project.";
      setError(message);
      toast({
        position: "top",
        title: "Unable to add item",
        description: message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const buildAppToAdd = (project, appName, storeId, androidId) => {
    const normalizedName = appName.trim();
    const normalizedStoreId = storeId.trim();
    const normalizedAndroidId = androidId.trim();
    const nextApp = createAppState();

    return {
      ...nextApp,
      id: buildUniqueAppId(normalizedName, project.apps) || nextApp.id,
      name: normalizedName,
      platforms: {
        ios: {
          ...nextApp.platforms.ios,
          sourceId: normalizedStoreId,
        },
        android: {
          ...nextApp.platforms.android,
          sourceId: normalizedAndroidId,
        },
      },
    };
  };

  const openRemoveAppConfirmation = (projectIndex, appIndex) => {
    setRemoveAppTarget({ projectIndex, appIndex });
    setRemoveAppConfirmation("");
    setRemoveAppTouched(false);
  };

  const closeRemoveAppConfirmation = () => {
    setRemoveAppTarget(null);
    setRemoveAppConfirmation("");
    setRemoveAppTouched(false);
  };

  const confirmRemoveApp = async () => {
    const appName = removeAppItem?.name?.trim() || "";
    const typedName = removeAppConfirmation.trim();

    if (!removeAppTarget || !appName || typedName !== appName) {
      setRemoveAppTouched(true);
      return;
    }

    try {
      setSaving(true);
      setError("");

      const targetProject = config.projects[removeAppTarget.projectIndex];

      if (!targetProject) {
        throw new Error("Project not found.");
      }

      const nextProject = {
        ...targetProject,
        apps: targetProject.apps.filter((_, index) => index !== removeAppTarget.appIndex),
      };

      await persistProject(nextProject, true);
      closeRemoveAppConfirmation();
    } catch (saveError) {
      console.error("Failed to remove app:", saveError);
      const message = saveError.message || "Unable to remove app.";
      setError(message);
      toast({
        position: "top",
        title: "Unable to remove item",
        description: message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const openAddAppDialog = (projectIndex) => {
    setNewAppDialog({ projectIndex });
    setNewAppName("");
    setNewAppNameTouched(false);
    setNewAppStoreId("");
    setNewAppStoreIdTouched(false);
    setNewAppAndroidId("");
    setNewAppAndroidIdTouched(false);
  };

  const closeAddAppDialog = () => {
    setNewAppDialog(null);
    setNewAppName("");
    setNewAppNameTouched(false);
    setNewAppStoreId("");
    setNewAppStoreIdTouched(false);
    setNewAppAndroidId("");
    setNewAppAndroidIdTouched(false);
  };

  const confirmAddApp = async () => {
    const trimmedName = newAppName.trim();
    const trimmedStoreId = newAppStoreId.trim();
    const trimmedAndroidId = newAppAndroidId.trim();

    if (!newAppDialog || !trimmedName || !trimmedStoreId || !trimmedAndroidId) {
      setNewAppNameTouched(true);
      setNewAppStoreIdTouched(true);
      setNewAppAndroidIdTouched(true);
      return;
    }

    try {
      setSaving(true);
      setError("");

      const targetProject = config.projects[newAppDialog.projectIndex];

      if (!targetProject) {
        throw new Error("Project not found.");
      }

      if (hasAppSlugConflict(trimmedName, targetProject.apps)) {
        throw new Error("An app with this name already exists in this project.");
      }

      const nextProject = {
        ...targetProject,
        apps: [
          ...targetProject.apps,
          buildAppToAdd(targetProject, trimmedName, trimmedStoreId, trimmedAndroidId),
        ],
      };

      await persistProject(nextProject, true);
      closeAddAppDialog();
    } catch (saveError) {
      console.error("Failed to add app:", saveError);
      const message = saveError.message || "Unable to add app.";
      setError(message);
      toast({
        position: "top",
        title: "Unable to add item",
        description: message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");

    try {
      validateProjectsEffectiveDateTimes(config.projects);
      await persistProjects(config.projects.map(sanitizeProjectForSave), true);
    } catch (saveError) {
      console.error("Failed to save update dashboard:", saveError);
      const message = saveError.message || "Unable to save update dashboard.";
      setError(message);

      toast({
        position: "top",
        title: "Unable to save changes",
        description: message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFromModal = async () => {
    if (!selectedProject) return;

    setSaving(true);
    setError("");

    try {
      validateProjectsEffectiveDateTimes([selectedProject]);
      const sanitizedProject = sanitizeProjectForSave(selectedProject);
      await persistProject(sanitizedProject, true);
      const sanitizedSelectedApp = sanitizedProject.apps?.[selectedAppEditor?.appIndex ?? -1] || null;
      setSelectedAppInitialSnapshot(sanitizedSelectedApp ? JSON.stringify(normalizeApp(sanitizedSelectedApp)) : "");
    } catch (saveError) {
      console.error("Failed to save app update:", saveError);
      const message = saveError.message || "Unable to save app update.";
      setError(message);

      toast({
        position: "top",
        title: "Unable to save changes",
        description: message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const persistProject = async (project, showSuccessToast = true) => {
    const auth = getAuth();
    const updatedBy = auth.currentUser?.email || null;
    const projectRef = doc(db, APP_UPDATE_COLLECTION, project.id);
    const appsCollectionRef = collection(projectRef, "apps");
    const existingAppsSnapshot = await getDocs(appsCollectionRef);
    const existingAppIds = new Set(existingAppsSnapshot.docs.map((appDoc) => appDoc.id));
    const batch = writeBatch(db);

    batch.set(projectRef, {
      name: project.name,
      description: project.description,
      projectRules: deleteField(),
      updatedBy,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    project.apps.forEach((app) => {
      existingAppIds.delete(app.id);
      batch.set(doc(appsCollectionRef, app.id), {
        name: app.name,
        appKey: app.appKey,
        description: app.description,
        platforms: app.platforms,
        updatedBy,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    });

    existingAppIds.forEach((appId) => {
      batch.delete(doc(appsCollectionRef, appId));
    });

    await batch.commit();

    setConfig((currentConfig) => {
      const nextProjects = currentConfig.projects.map((currentProject) =>
        currentProject.id === project.id
          ? {
            ...project,
            updatedAt: new Date().toISOString(),
            updatedBy,
          }
          : currentProject
      );

      return normalizeDashboardConfig(nextProjects);
    });
    if (showSuccessToast) {
      toast({
        position: "top",
        title: "Update settings saved",
        description: "App update rules were saved successfully.",
        status: "success",
        isClosable: true,
      });
    }
  };

  const deleteProjectCascade = async (projectId) => {
    const projectRef = doc(db, APP_UPDATE_COLLECTION, projectId);
    const appsSnapshot = await getDocs(collection(projectRef, "apps"));
    const batch = writeBatch(db);

    appsSnapshot.docs.forEach((appDoc) => {
      batch.delete(appDoc.ref);
    });

    batch.delete(projectRef);
    await batch.commit();
  };

  const persistProjects = async (projects, showSuccessToast = true) => {
    const existingProjectsSnapshot = await getDocs(collection(db, APP_UPDATE_COLLECTION));
    const currentProjectIds = new Set(projects.map((project) => project.id));

    for (const project of projects) {
      // eslint-disable-next-line no-await-in-loop
      await persistProject(project, false);
    }

    for (const projectDoc of existingProjectsSnapshot.docs) {
      if (!currentProjectIds.has(projectDoc.id)) {
        // eslint-disable-next-line no-await-in-loop
        await deleteProjectCascade(projectDoc.id);
      }
    }

    setConfig(normalizeDashboardConfig(projects));
    if (showSuccessToast) {
      toast({
        position: "top",
        title: "Update settings saved",
        description: "App update rules were saved successfully.",
        status: "success",
        isClosable: true,
      });
    }
  };

  const openAppEditor = (projectIndex, appIndex) => {
    const appToEdit = config.projects?.[projectIndex]?.apps?.[appIndex] || null;
    setSelectedAppInitialSnapshot(appToEdit ? JSON.stringify(normalizeApp(appToEdit)) : "");
    setSelectedAppEditor({ projectIndex, appIndex });
  };

  const closeAppEditor = () => {
    setConfirmCloseAppEditorOpen(false);
    setSelectedAppInitialSnapshot("");
    setSelectedAppEditor(null);
  };

  const selectedProject = selectedAppEditor
    ? config.projects?.[selectedAppEditor.projectIndex]
    : null;
  const selectedApp = selectedProject && selectedAppEditor
    ? selectedProject.apps?.[selectedAppEditor.appIndex]
    : null;
  const selectedAppCurrentSnapshot = selectedApp ? JSON.stringify(normalizeApp(selectedApp)) : "";
  const hasSelectedAppChanges = Boolean(
    selectedAppEditor &&
    selectedAppInitialSnapshot &&
    selectedAppCurrentSnapshot !== selectedAppInitialSnapshot
  );
  const removeProjectItem = removeProjectTarget
    ? config.projects?.[removeProjectTarget.projectIndex]
    : null;
  const removeAppProject = removeAppTarget
    ? config.projects?.[removeAppTarget.projectIndex]
    : null;
  const removeAppItem = removeAppProject && removeAppTarget
    ? removeAppProject.apps?.[removeAppTarget.appIndex]
    : null;
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const updateCandidates = findAppUpdateCandidates(releaseApps, config.projects);
  const filteredProjects = config.projects.map((project, projectIndex) => {
    const filteredApps = (project.apps || []).filter((app) => {
      const appName = (app.name || "").toLowerCase();
      return !normalizedSearchTerm || appName.includes(normalizedSearchTerm);
    }).sort((a, b) => {
      const comparison = getAppDisplayName(a).localeCompare(getAppDisplayName(b));
      return appSortDirection === "asc" ? comparison : -comparison;
    });

    return {
      project,
      projectIndex,
      filteredApps,
    };
  }).filter(({ filteredApps }) => filteredApps.length > 0);

  const requestCloseAppEditor = () => {
    if (hasSelectedAppChanges) {
      setConfirmCloseAppEditorOpen(true);
      return;
    }

    closeAppEditor();
  };

  const discardAppEditorChanges = () => {
    if (selectedAppEditor && selectedAppInitialSnapshot) {
      const restoredApp = JSON.parse(selectedAppInitialSnapshot);

      setConfig((current) => ({
        ...current,
        projects: current.projects.map((project, projectIndex) => {
          if (projectIndex !== selectedAppEditor.projectIndex) {
            return project;
          }

          return {
            ...project,
            apps: project.apps.map((app, appIndex) => (
              appIndex === selectedAppEditor.appIndex ? normalizeApp(restoredApp) : app
            )),
          };
        }),
      }));
    }

    closeAppEditor();
  };

  return (
    <div className="update-dashboard-page">
      <PortalHeader>
          <button onClick={() => navigate("/app-list")} className="update-nav-button">
            App releases
          </button>
          <button onClick={handleLogout} className="update-logout-button">
            Sign out
          </button>
      </PortalHeader>

      <main className="update-dashboard-main">
        {loading && (
          <div className="loading">
            <LineWave
              visible={true}
              height="100"
              width="100"
              color="#FE8100"
              ariaLabel="line-wave-loading"
            />
          </div>
        )}

        {!loading && (
          <>
            <section className="dashboard-hero">
              <div>
                <p className="dashboard-kicker">Update management</p>
                <h1>App updates</h1>
                <p className="dashboard-description">
                  Synchronize version information, manage release notes, and configure minimum supported versions for required updates.
                </p>
              </div>

              <div className="dashboard-meta-card">
                <span className="meta-label">Last updated</span>
                <strong>{config.updatedAt ? new Date(config.updatedAt).toLocaleString("en-US") : "No changes yet"}</strong>
                <span className="meta-label">Updated by</span>
                <strong>{config.updatedBy || "-"}</strong>
              </div>
            </section>

            <section className="dashboard-actions">
              <div className="dashboard-actions-group">
                <button onClick={openAddProjectDialog} className="secondary-action-button">
                  Add project
                </button>
              </div>
              <div className="dashboard-actions-group dashboard-actions-group-right">
                <button
                  onClick={handleSave}
                  className={`primary-action-button dashboard-save-button is-dirty`}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
                <button
                  onClick={handleSyncAllMetadata}
                  className="secondary-action-button"
                  disabled={saving || syncingKey === "all"}
                >
                  {syncingKey === "all" ? "Synchronizing version information…" : "Sync version information"}
                </button>
              </div>
            </section>

            {error ? <p className="error">{error}</p> : null}

            <details className="update-candidates">
              <summary>
                Android apps without update settings{!releaseAppsLoading && !releaseAppsError && !error ? ` (${updateCandidates.length})` : ""}
              </summary>
              <p className="update-candidates-description">
                These releases have no matching Android update configuration. Use the listed app IDs to add their update settings. Releases without an app ID are excluded.
              </p>
              {releaseAppsLoading ? <p>Checking update settings…</p> : releaseAppsError || error ? (
                <p role="alert">{releaseAppsError || "Resolve the dashboard error before reviewing this comparison."}</p>
              ) : updateCandidates.length === 0 ? (
                <p>All eligible Android releases have update settings.</p>
              ) : (
                <ul className="update-candidates-list">
                  {updateCandidates.map((candidate) => (
                    <li key={candidate.id}>
                      <strong>{candidate.appName || candidate.id}</strong>
                      <span>App ID: {candidate.sourceId || "Missing"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </details>

            <section className="dashboard-filters" aria-label="Update dashboard filters">
              <label className="field dashboard-filter-field">
                <span>Search by name</span>
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search apps by name"
                />
              </label>
              <button
                type="button"
                className="secondary-action-button"
                aria-pressed={appSortDirection === "desc"}
                onClick={() => setAppSortDirection((current) => current === "asc" ? "desc" : "asc")}
              >
                Sort: {appSortDirection === "asc" ? "Ascending" : "Descending"}
              </button>
            </section>

            {config.projects.length === 0 && (
              <div className="empty-dashboard-state">
                <p className="section-label">No projects</p>
                <h2>Create your first project to start managing update rules.</h2>
              </div>
            )}

            {config.projects.length > 0 && filteredProjects.length === 0 && (
              <div className="empty-dashboard-state">
                <p className="section-label">No matching apps</p>
                <h2>No apps match the current search.</h2>
              </div>
            )}

            <div className="project-list">
              {filteredProjects.map(({ project, projectIndex, filteredApps }) => (
                <section key={project.id || `project-${projectIndex}`} className="project-card">
                  <div className="project-card-header">
                    <div>
                      <p className="section-label">Project</p>
                      <h2>{project.name || ""}</h2>
                    </div>
                    <button
                      onClick={() => openRemoveProjectConfirmation(projectIndex)}
                      className="ghost-danger-button"
                      type="button"
                    >
                      Remove project
                    </button>
                  </div>

                  <div className="form-grid">
                    <label className="field">
                      <span>Project name</span>
                      <input
                        type="text"
                        value={project.name}
                        onChange={(event) => updateProjectField(projectIndex, "name", event.target.value)}
                        placeholder="Project name"
                      />
                    </label>

                    <label className="field field-wide">
                      <span>Project description</span>
                      <input
                        type="text"
                        value={project.description || ""}
                        onChange={(event) => updateProjectField(projectIndex, "description", event.target.value)}
                        placeholder="Project description"
                      />
                    </label>
                  </div>

                  <div className="global-rules-section">
                    <div className="global-rules-header bulk-apply-header">
                      <div>
                        <p className="section-label">Bulk updates</p>
                        <h3>Apply the same update rule to selected apps</h3>
                      </div>
                      <button
                        type="button"
                        className="secondary-action-button bulk-apply-toggle"
                        onClick={() => toggleBulkApplyPanel(project.id)}
                      >
                        {expandedBulkApplyByProject?.[project.id] ? "Hide bulk updates" : "Show bulk updates"}
                      </button>
                    </div>

                    {expandedBulkApplyByProject?.[project.id] && (
                      <>
                        <label className="field inline-checkbox bulk-select-all">
                          <input
                            type="checkbox"
                            checked={filteredApps.length > 0 && filteredApps.every((app) => getSelectedBulkAppIds(project.id).includes(app.id))}
                            onChange={() => toggleSelectAllBulkApps(project.id, filteredApps)}
                          />
                          <span>Select visible apps</span>
                        </label>

                        <div className="platform-grid">
                          {["ios", "android"].map((platform) => {
                            const platformLabel = platform === "ios" ? "iOS" : "Android";
                            const bulkRule = getBulkRuleDraft(project.id, platform);
                            const selectedCount = getSelectedBulkAppIds(project.id).length;

                            return (
                              <section key={`bulk-${platform}`} className="platform-card bulk-apply-card">
                                <div className="platform-card-header">
                                  <h4>{platformLabel}</h4>
                                </div>

                                <label className="field">
                                  <span>Minimum supported version</span>
                                  <input
                                    type="text"
                                    value={bulkRule.minimumSupportedVersion}
                                    onChange={(event) =>
                                      updateGlobalRuleField(projectIndex, platform, "minimumSupportedVersion", event.target.value)
                                    }
                                    placeholder="Minimum supported version"
                                  />
                                </label>

                                {platform === "android" && (
                                  <label className="field">
                                    <span>Minimum supported version code</span>
                                    <input
                                      type="text"
                                      value={bulkRule.minimumSupportedVersionCode}
                                      onChange={(event) =>
                                        updateGlobalRuleField(projectIndex, platform, "minimumSupportedVersionCode", event.target.value)
                                      }
                                      placeholder="Minimum supported version code"
                                    />
                                  </label>
                                )}

                                <label className="field">
                                  <span>Required update effective date</span>
                                  <div className="field-split-row">
                                    <input
                                      type="date"
                                      value={getEffectiveDatePart(bulkRule.minimumSupportedEffectiveDate)}
                                      onChange={(event) =>
                                        updateGlobalRuleField(
                                          projectIndex,
                                          platform,
                                          "minimumSupportedEffectiveDate",
                                          updateEffectiveDateTimePart(bulkRule.minimumSupportedEffectiveDate, "date", event.target.value)
                                        )
                                      }
                                    />
                                    <input
                                      type="time"
                                      value={getEffectiveTimePart(bulkRule.minimumSupportedEffectiveDate)}
                                      onChange={(event) =>
                                        updateGlobalRuleField(
                                          projectIndex,
                                          platform,
                                          "minimumSupportedEffectiveDate",
                                          updateEffectiveDateTimePart(bulkRule.minimumSupportedEffectiveDate, "time", event.target.value)
                                        )
                                      }
                                    />
                                    <button
                                      type="button"
                                      className="field-clear-button"
                                      onClick={() => updateGlobalRuleField(projectIndex, platform, "minimumSupportedEffectiveDate", "")}
                                    >
                                      Clear
                                    </button>
                                  </div>
                                </label>

                                <label className="field">
                                  <span>Release notes</span>
                                  <textarea
                                    value={bulkRule.whatsNew}
                                    onChange={(event) =>
                                      updateGlobalRuleField(projectIndex, platform, "whatsNew", event.target.value)
                                    }
                                    placeholder="Describe the changes in this release"
                                  />
                                </label>

                                <button
                                  type="button"
                                  className="secondary-action-button bulk-apply-submit"
                                  disabled={selectedCount === 0}
                                  onClick={() => {
                                    try {
                                      requestBulkApplyToSelectedApps(projectIndex, platform);
                                    } catch (bulkError) {
                                      const message = bulkError.message || "Unable to apply update rules.";
                                      setError(message);
                                      toast({
                                        position: "top",
                                        title: "Unable to apply update rules",
                                        description: message,
                                        status: "error",
                                        isClosable: true,
                                      });
                                    }
                                  }}
                                >
                                  {selectedCount === 0 ? "Select apps first" : `Apply to ${selectedCount} selected app${selectedCount === 1 ? "" : "s"}`}
                                </button>
                              </section>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="apps-stack">
                    <button onClick={() => openAddAppDialog(projectIndex)} className="secondary-action-button" type="button">
                      Add app
                    </button>

                    <div className="app-card-grid">
                      {filteredApps.map((app) => {
                        const appIndex = project.apps.findIndex((projectApp) => projectApp.id === app.id);

                        return (
                          <article
                            key={app.id || `app-${appIndex}`}
                            className="app-config-card"
                          >
                            {expandedBulkApplyByProject?.[project.id] && (
                              <label className="app-select-checkbox" onClick={(event) => event.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={getSelectedBulkAppIds(project.id).includes(app.id)}
                                  onChange={() => toggleBulkAppSelection(project.id, app.id)}
                                />
                                <span>Select</span>
                              </label>
                            )}
                            <button
                              type="button"
                              className="app-card-surface"
                              onClick={() => openAppEditor(projectIndex, appIndex)}
                            >
                                <div className="app-summary-copy">
                                  <h3>{getAppDisplayName(app)}</h3>
                                </div>

                              <div className="app-summary-platforms">
                                {["ios", "android"].map((platform) => {
                                  const platformConfig = app.platforms?.[platform] || createPlatformState(platform);
                                  const platformLabel = platform === "ios" ? "iOS" : "Android";
                                  const effectiveRule = normalizeRule(platformConfig.rule);

                                  return (
                                    <div key={platform} className="app-summary-platform-card">
                                      <div className="app-summary-platform-header">
                                        <span className="app-summary-platform-title">{platformLabel}</span>
                                        <span className={`app-status-pill ${platformConfig.enabled ? "enabled" : "disabled"}`}>
                                          {platformConfig.enabled ? "Enabled" : "Disabled"}
                                        </span>
                                      </div>
                                      <p className="app-summary-platform-line">
                                        Source: {platformConfig.displayName || platformConfig.sourceId || "Not set"}
                                      </p>
                                      <p className="app-summary-platform-line">
                                        Latest: {platformConfig.latestVersion || "-"}
                                      </p>
                                      <p className="app-summary-platform-line">
                                        Latest version code: {platformConfig.latestVersionCode || "-"}
                                      </p>
                                      <p className="app-summary-platform-line">
                                        Required update: {formatRuleWithEffectiveDate(
                                          platform === "android"
                                            ? formatVersionWithCode(
                                              effectiveRule.minimumSupportedVersion,
                                              effectiveRule.minimumSupportedVersionCode
                                            )
                                            : effectiveRule.minimumSupportedVersion,
                                          formatEffectiveDateLabel(effectiveRule.minimumSupportedEffectiveDate)
                                        )}
                                      </p>
                                      <p className="app-summary-platform-line">
                                        Release notes: {effectiveRule.whatsNew || "Not set"}
                                      </p>
                                    </div>
                                  );
                                })}
                              </div>
                            </button>

                            <button
                              onClick={() => openRemoveAppConfirmation(projectIndex, appIndex)}
                              className="remove-app-icon-button"
                              type="button"
                              aria-label={`Remove ${app.name || `App ${appIndex + 1}`}`}
                            >
                              <CloseIcon boxSize={2.5} />
                            </button>
                          </article>
                        );
                      })}
                    </div>
                  </div>
                </section>
              ))}
            </div>
          </>
        )}
      </main>

      <Modal isOpen={Boolean(selectedAppEditor && selectedProject && selectedApp)} onClose={requestCloseAppEditor} size="6xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(6px)" />
        <ModalContent borderRadius="24px" mx="16px">
          <ModalHeader>{selectedApp?.name || "App update settings"}</ModalHeader>
          <ModalCloseButton onClick={requestCloseAppEditor} />
          <ModalBody pb={6}>
            {selectedProject && selectedApp && selectedAppEditor && (
              <>
                <div className="form-grid">
                  <label className="field">
                    <span>App name</span>
                    <input
                      type="text"
                      value={selectedApp.name}
                      onChange={(event) => updateAppField(selectedAppEditor.projectIndex, selectedAppEditor.appIndex, "name", event.target.value)}
                      placeholder="App name"
                    />
                  </label>

                  <label className="field field-wide">
                    <span>App description</span>
                    <input
                      type="text"
                      value={selectedApp.description || ""}
                      onChange={(event) => updateAppField(selectedAppEditor.projectIndex, selectedAppEditor.appIndex, "description", event.target.value)}
                      placeholder="App description"
                    />
                  </label>
                </div>

                <div className="platform-grid">
                  {["ios", "android"].map((platform) => {
                    const platformConfig = selectedApp.platforms?.[platform] || createPlatformState(platform);
                    const platformLabel = platform === "ios" ? "iOS" : "Android";
                    const displayedOverrideRule = normalizeRule(platformConfig.rule);
                    const effectiveRuleSummary = getEffectiveRuleSummary(
                      platformConfig.rule,
                      platformConfig.latestVersion,
                      platformConfig.latestVersionCode,
                      platform
                    );

                    return (
                      <section key={platform} className="platform-card">
                        <div className="platform-card-header">
                          <h4>{platformLabel}</h4>
                          <label className="platform-toggle">
                            <input
                              type="checkbox"
                              checked={platformConfig.enabled}
                              onChange={(event) =>
                                updatePlatformField(selectedAppEditor.projectIndex, selectedAppEditor.appIndex, platform, "enabled", event.target.checked)
                              }
                            />
                            <span>Enabled</span>
                          </label>
                        </div>

                        <label className="field">
                          <span>{platform === "ios" ? "App Store app ID" : "Android app ID"}</span>
                          <input
                            type="text"
                            value={platformConfig.sourceId || ""}
                            onChange={(event) =>
                              updatePlatformField(selectedAppEditor.projectIndex, selectedAppEditor.appIndex, platform, "sourceId", event.target.value)
                            }
                            placeholder={platform === "ios" ? "App Store app ID" : "Android app ID"}
                          />
                          <small>
                            {platform === "ios"
                              ? "Used to fetch the app name, bundle ID, and latest App Store version."
                              : "Identifies the Android app in the release dashboard."}
                          </small>
                        </label>

                        <div className="platform-lookup-row">
                          <button
                            type="button"
                            className="secondary-action-button"
                            disabled={syncingKey === `${selectedAppEditor.projectIndex}-${selectedAppEditor.appIndex}-${platform}`}
                            onClick={() => handlePlatformSync(selectedAppEditor.projectIndex, selectedAppEditor.appIndex, platform)}
                          >
                            {syncingKey === `${selectedAppEditor.projectIndex}-${selectedAppEditor.appIndex}-${platform}` ? "Synchronizing…" : `Sync ${platformLabel} information`}
                          </button>
                        </div>

                        <div className="platform-metadata">
                          <div className="metadata-item">
                            <span>Latest available version</span>
                            <strong>{platformConfig.latestVersion || "-"}</strong>
                          </div>
                          <div className="metadata-item">
                            <span>Latest version code</span>
                            <strong>{platformConfig.latestVersionCode || "-"}</strong>
                          </div>
                          <div className="metadata-item">
                            <span>Last updated</span>
                            <strong>{getPlatformLastUpdatedDisplay(platformConfig)}</strong>
                          </div>
                        </div>

                        <label className="field">
                          <span>{platform === "ios" ? "Bundle ID" : "Package name"}</span>
                          <input
                            type="text"
                            value={platformConfig.identifier || ""}
                            readOnly
                            placeholder={platform === "ios" ? "Bundle identifier" : "Package name"}
                          />
                          <small>Populated during synchronization and cannot be edited.</small>
                        </label>

                        <label className="field">
                          <span>Store or update URL (optional)</span>
                          <input
                            type="text"
                            value={platformConfig.storeUrl || ""}
                            onChange={(event) =>
                              updatePlatformField(selectedAppEditor.projectIndex, selectedAppEditor.appIndex, platform, "storeUrl", event.target.value)
                            }
                            placeholder="Optional store or update URL"
                          />
                        </label>

                        <div className="rule-preview">
                          <span className="meta-label">Current update behavior</span>
                          <strong>
                            Update type: {effectiveRuleSummary.activeRuleLabel}
                          </strong>
                          <strong>
                            Version: {effectiveRuleSummary.activeVersionLabel}
                          </strong>
                          <strong>
                            Release notes: {effectiveRuleSummary.whatsNew}
                          </strong>
                        </div>

                        <div className="override-rule-section">
                          <label className="field">
                            <span>Minimum supported version</span>
                            <input
                              type="text"
                              value={displayedOverrideRule.minimumSupportedVersion}
                              onChange={(event) =>
                                updatePlatformRuleField(selectedAppEditor.projectIndex, selectedAppEditor.appIndex, platform, "minimumSupportedVersion", event.target.value)
                              }
                              placeholder="Minimum supported version"
                            />
                          </label>

                          {platform === "android" && (
                            <label className="field">
                              <span>Minimum supported version code</span>
                              <input
                                type="text"
                                value={displayedOverrideRule.minimumSupportedVersionCode}
                                onChange={(event) =>
                                  updatePlatformRuleField(selectedAppEditor.projectIndex, selectedAppEditor.appIndex, platform, "minimumSupportedVersionCode", event.target.value)
                                }
                                placeholder="Minimum supported version code"
                              />
                            </label>
                          )}

                          <label className="field">
                            <span>Required update effective date</span>
                            <div className="field-split-row">
                              <input
                                type="date"
                                value={getEffectiveDatePart(displayedOverrideRule.minimumSupportedEffectiveDate)}
                                onChange={(event) =>
                                  updatePlatformRuleField(
                                    selectedAppEditor.projectIndex,
                                    selectedAppEditor.appIndex,
                                    platform,
                                    "minimumSupportedEffectiveDate",
                                    updateEffectiveDateTimePart(displayedOverrideRule.minimumSupportedEffectiveDate, "date", event.target.value)
                                  )
                                }
                              />
                              <input
                                type="time"
                                value={getEffectiveTimePart(displayedOverrideRule.minimumSupportedEffectiveDate)}
                                onChange={(event) =>
                                  updatePlatformRuleField(
                                    selectedAppEditor.projectIndex,
                                    selectedAppEditor.appIndex,
                                    platform,
                                    "minimumSupportedEffectiveDate",
                                    updateEffectiveDateTimePart(displayedOverrideRule.minimumSupportedEffectiveDate, "time", event.target.value)
                                  )
                                }
                              />
                              <button
                                type="button"
                                className="field-clear-button"
                                onClick={() => updatePlatformRuleField(selectedAppEditor.projectIndex, selectedAppEditor.appIndex, platform, "minimumSupportedEffectiveDate", "")}
                              >
                                Clear
                              </button>
                            </div>
                            <small>Optional. Enter both a date and time in MDT (UTC−06:00).</small>
                          </label>

                          <label className="field">
                            <span>Release notes</span>
                            <textarea
                              value={displayedOverrideRule.whatsNew}
                              onChange={(event) =>
                                updatePlatformRuleField(selectedAppEditor.projectIndex, selectedAppEditor.appIndex, platform, "whatsNew", event.target.value)
                              }
                              placeholder="Describe the changes in this release"
                            />
                          </label>
                        </div>
                      </section>
                    );
                  })}
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter gap={3}>
            <button type="button" className="secondary-action-button modal-action-button" onClick={requestCloseAppEditor}>
              Close
            </button>
            <button
              type="button"
              className="primary-action-button modal-action-button"
              onClick={handleSaveFromModal}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save update settings"}
            </button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={confirmCloseAppEditorOpen} onClose={() => setConfirmCloseAppEditorOpen(false)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Discard changes?</AlertDialogHeader>
            <AlertDialogBody>
              <p>Closing the editor will discard your unsaved changes to this app’s update settings.</p>
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <button
                type="button"
                className="secondary-action-button modal-action-button"
                onClick={() => setConfirmCloseAppEditorOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ghost-danger-button modal-action-button"
                onClick={discardAppEditorChanges}
              >
                Discard changes
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog isOpen={Boolean(pendingBulkApply)} onClose={() => setPendingBulkApply(null)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Apply update rules?</AlertDialogHeader>
            <AlertDialogBody>
              <p>
                {`Apply this ${pendingBulkApply?.platform === "ios" ? "iOS" : "Android"} update rule to ${pendingBulkApply?.count || 0} app${pendingBulkApply?.count === 1 ? "" : "s"}?`}
              </p>
              {pendingBulkApply?.appNames?.length ? (
                <div className="bulk-apply-confirm-list">
                  {pendingBulkApply.appNames.map((appName) => (
                    <p key={appName}>{appName}</p>
                  ))}
                </div>
              ) : null}
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <button
                type="button"
                className="secondary-action-button modal-action-button"
                onClick={() => setPendingBulkApply(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-action-button modal-action-button"
                onClick={confirmBulkApplyToSelectedApps}
                disabled={saving}
              >
                {saving ? "Applying…" : "Apply and save"}
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog isOpen={Boolean(removeAppTarget)} onClose={closeRemoveAppConfirmation} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Remove app</AlertDialogHeader>
            <AlertDialogBody>
              <p>
                To remove <strong>{removeAppItem?.name || "this app"}</strong> from <strong>{removeAppProject?.name || "this project"}</strong>, type the app name below to confirm.
              </p>
              <FormControl isInvalid={removeAppTouched && removeAppConfirmation.trim() !== (removeAppItem?.name?.trim() || "")} mt={4}>
                <Input
                  value={removeAppConfirmation}
                  onChange={(event) => setRemoveAppConfirmation(event.target.value)}
                  onBlur={() => setRemoveAppTouched(true)}
                  placeholder="App name"
                  autoFocus
                />
                <FormErrorMessage>App name must match exactly.</FormErrorMessage>
              </FormControl>
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <button type="button" className="secondary-action-button modal-action-button" onClick={closeRemoveAppConfirmation}>
                Cancel
              </button>
              <button type="button" className="ghost-danger-button modal-action-button" onClick={confirmRemoveApp}>
                Remove
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog isOpen={Boolean(removeProjectTarget)} onClose={closeRemoveProjectConfirmation} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Remove project</AlertDialogHeader>
            <AlertDialogBody>
              <p>
                To remove <strong>{removeProjectItem?.name || "this project"}</strong>, type the project name below to confirm.
              </p>
              <FormControl isInvalid={removeProjectTouched && removeProjectConfirmation.trim() !== (removeProjectItem?.name?.trim() || "")} mt={4}>
                <Input
                  value={removeProjectConfirmation}
                  onChange={(event) => setRemoveProjectConfirmation(event.target.value)}
                  onBlur={() => setRemoveProjectTouched(true)}
                  placeholder="Project name"
                  autoFocus
                />
                <FormErrorMessage>Project name must match exactly.</FormErrorMessage>
              </FormControl>
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <button type="button" className="secondary-action-button modal-action-button" onClick={closeRemoveProjectConfirmation}>
                Cancel
              </button>
              <button type="button" className="ghost-danger-button modal-action-button" onClick={confirmRemoveProject}>
                Remove project
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog isOpen={newProjectDialogOpen} onClose={closeAddProjectDialog} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Add project</AlertDialogHeader>
            <AlertDialogBody>
              <FormControl isInvalid={newProjectNameTouched && !newProjectName.trim()}>
                <Input
                  value={newProjectName}
                  onChange={(event) => setNewProjectName(event.target.value)}
                  onBlur={() => setNewProjectNameTouched(true)}
                  placeholder="Project name"
                  autoFocus
                />
                <FormErrorMessage>Project name is required.</FormErrorMessage>
              </FormControl>
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <button type="button" className="secondary-action-button modal-action-button" onClick={closeAddProjectDialog}>
                Cancel
              </button>
              <button type="button" className="primary-action-button modal-action-button" onClick={confirmAddProject}>
                Save Project
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog isOpen={Boolean(newAppDialog)} onClose={closeAddAppDialog} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Add app</AlertDialogHeader>
            <AlertDialogBody>
              <FormControl isInvalid={newAppNameTouched && !newAppName.trim()}>
                <Input
                  value={newAppName}
                  onChange={(event) => setNewAppName(event.target.value)}
                  onBlur={() => setNewAppNameTouched(true)}
                  placeholder="App name"
                  autoFocus
                />
                <FormErrorMessage>App name is required.</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={newAppStoreIdTouched && !newAppStoreId.trim()} mt={4}>
                <Input
                  value={newAppStoreId}
                  onChange={(event) => setNewAppStoreId(event.target.value)}
                  onBlur={() => setNewAppStoreIdTouched(true)}
                  placeholder="App Store app ID"
                />
                <FormErrorMessage>App Store app ID is required.</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={newAppAndroidIdTouched && !newAppAndroidId.trim()} mt={4}>
                <Input
                  value={newAppAndroidId}
                  onChange={(event) => setNewAppAndroidId(event.target.value)}
                  onBlur={() => setNewAppAndroidIdTouched(true)}
                  placeholder="Android app ID"
                />
                <FormErrorMessage>Android app ID is required.</FormErrorMessage>
              </FormControl>
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <button type="button" className="secondary-action-button modal-action-button" onClick={closeAddAppDialog}>
                Cancel
              </button>
              <button type="button" className="primary-action-button modal-action-button" onClick={confirmAddApp}>
                Save App
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </div>
  );
};

export default UpdateDashboard;
